import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  CanonicalSkillAutoCompleteResultItem,
  JobseekerSkillViewModel,
  SkillAutoCompleteResult,
  UserTypeEnum,
} from '@hrra/common-models';
import { ButtonTypeAttributeEnum, ButtonTypeEnum } from '@hrra/ui';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

//presentational skills edit form; the hosting dialog shell owns the state and reacts to the outputs
@Component({
    selector: 'hrra-jobseeker-skills-edit-form',
    templateUrl: './jobseeker-skills-edit-form.component.html',
    styleUrls: ['./jobseeker-skills-edit-form.component.scss'],
    standalone: false
})
export class JobseekerSkillsEditFormComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() title: string;
  @Input() draftSkills: JobseekerSkillViewModel[] | null;
  @Input() suggestions: SkillAutoCompleteResult | null;
  @Input() hasExactMatch: boolean;
  @Input() saveDisabled: boolean;
  @Input() userType: UserTypeEnum = UserTypeEnum.Jobseeker;

  @Output() onQueryChange = new EventEmitter<string>();
  @Output() onSelectSkill = new EventEmitter<CanonicalSkillAutoCompleteResultItem>();
  @Output() onAddCustomSkill = new EventEmitter<string>();
  @Output() onRemoveSkill = new EventEmitter<number>();
  @Output() onReorderSkills = new EventEmitter<{ previousIndex: number; currentIndex: number }>();
  @Output() onSave = new EventEmitter<void>();

  skillInput = new UntypedFormControl('');

  public buttonType: any = ButtonTypeEnum;
  public buttonAttributeType: any = ButtonTypeAttributeEnum;

  ngOnInit(): void {
    this.skillInput.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((query) => this.onQueryChange.emit(query ?? ''));
  }

  selectSkill(skill: CanonicalSkillAutoCompleteResultItem) {
    this.onSelectSkill.emit(skill);
    this.skillInput.setValue('', { emitEvent: false });
  }

  addCustomSkill() {
    if (this.canAddCustomSkill) {
      this.onAddCustomSkill.emit(this.skillInput.value.trim());
      this.skillInput.setValue('', { emitEvent: false });
    }
  }

  removeSkill(index: number) {
    this.onRemoveSkill.emit(index);
    this.onQueryChange.emit(this.skillInput.value ?? '');
  }

  reorderSkills(event: { previousIndex: number; currentIndex: number }) {
    this.onReorderSkills.emit(event);
  }

  save() {
    if (!this.saveDisabled) {
      this.onSave.emit();
    }
  }

  public get canAddCustomSkill(): boolean {
    let query = (this.skillInput.value ?? '').trim().toLowerCase();
    if (!query || this.hasExactMatch) {
      return false;
    }
    return !this.draftSkills?.some((s) => s.name?.toLowerCase() === query || s.userProvidedText?.toLowerCase() === query);
  }

  //splits a suggestion name into the part matching the query (shown in bold) and the rest
  getMatchSplit(name: string): { pre: string; match: string; post: string } {
    let query = (this.skillInput.value ?? '').trim();
    let index = query ? name.toLowerCase().indexOf(query.toLowerCase()) : -1;
    if (index < 0) {
      return { pre: name, match: '', post: '' };
    }
    return {
      pre: name.slice(0, index),
      match: name.slice(index, index + query.length),
      post: name.slice(index + query.length),
    };
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
